import { Loader2 } from "lucide-react";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Input } from "@/components/ui/input";
import {
  EMBED_COST_PER_MILLION_USD,
  formatUsd,
  type Estimate,
} from "./useRagSettings";

type Props = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: string;
  description: string;
  /** Required confirmation phrase. */
  confirmPhrase: string;
  confirmText: string;
  onConfirmTextChange: (v: string) => void;
  estimate: Estimate | null;
  /** Reference chunk size (settings or current input). */
  referenceChunkTokens: number;
  busy: boolean;
  onConfirm: () => void;
  /** Action button label, e.g. "Re-embed workspace". */
  actionLabel: string;
  /** Whether this estimate has the "workspaces" line (platform-wide). */
  showWorkspacesLine?: boolean;
};

/** Generic re-embed confirmation dialog with cost estimate + typed confirmation. */
export const ReembedConfirmDialog = ({
  open, onOpenChange, title, description,
  confirmPhrase, confirmText, onConfirmTextChange,
  estimate, referenceChunkTokens, busy, onConfirm, actionLabel, showWorkspacesLine,
}: Props) => (
  <AlertDialog open={open} onOpenChange={(o) => { onOpenChange(o); if (!o) onConfirmTextChange(""); }}>
    <AlertDialogContent>
      <AlertDialogHeader>
        <AlertDialogTitle>{title}</AlertDialogTitle>
        <AlertDialogDescription>{description}</AlertDialogDescription>
      </AlertDialogHeader>
      <div className="rounded-md border bg-muted/30 px-3 py-2 text-sm">
        {estimate ? (
          <>
            <div><strong>Estimated cost:</strong> {formatUsd(estimate.estUsd)} in embedding credits</div>
            <div className="text-xs text-muted-foreground mt-1">
              {showWorkspacesLine ? `${estimate.workspaces} workspace${estimate.workspaces === 1 ? "" : "s"} · ` : ""}
              {estimate.docs} doc{estimate.docs === 1 ? "" : "s"} · ~{estimate.chunks.toLocaleString()} chunk{estimate.chunks === 1 ? "" : "s"} × {referenceChunkTokens} tok @ ${EMBED_COST_PER_MILLION_USD.toFixed(2)} / 1M
            </div>
          </>
        ) : (
          <span className="text-muted-foreground inline-flex items-center gap-2">
            <Loader2 className="h-3 w-3 animate-spin" /> Estimating…
          </span>
        )}
      </div>
      <p className="text-xs text-muted-foreground">
        Type <span className="font-mono font-semibold">{confirmPhrase}</span> to confirm.
      </p>
      <Input
        value={confirmText}
        onChange={(e) => onConfirmTextChange(e.target.value)}
        placeholder={confirmPhrase}
        autoFocus
      />
      <AlertDialogFooter>
        <AlertDialogCancel disabled={busy}>Cancel</AlertDialogCancel>
        <AlertDialogAction
          disabled={confirmText !== confirmPhrase || busy}
          onClick={(e) => { e.preventDefault(); onConfirm(); }}
        >
          {busy ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
          {actionLabel}
        </AlertDialogAction>
      </AlertDialogFooter>
    </AlertDialogContent>
  </AlertDialog>
);
